import { useState, useEffect, useRef } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import {
  Bell, BellRinging, Check, CheckCircle, Trash, SpeakerHigh, SpeakerSlash,
  AirplaneTilt, UserCirclePlus, CheckSquare, WarningCircle, X
} from '@phosphor-icons/react';

const NOTIF_ICONS = {
  new_incident: WarningCircle,
  assigned_investigation: UserCirclePlus,
  task_assigned: CheckSquare,
  task_completed: CheckCircle,
  incident_rejected: X,
};

const NOTIF_COLORS = {
  new_incident: 'text-red-500 bg-red-50',
  assigned_investigation: 'text-blue-500 bg-blue-50',
  task_assigned: 'text-purple-500 bg-purple-50',
  task_completed: 'text-green-500 bg-green-50',
  incident_rejected: 'text-amber-500 bg-amber-50',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function NotificationBell() {
  const {
    unreadCount, notifications, inboxOpen, setInboxOpen,
    soundEnabled, toggleSound,
    fetchNotifications, markRead, markAllRead, deleteNotification,
  } = useNotifications();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  // Load notifications when inbox opens
  useEffect(() => {
    if (inboxOpen && !loaded) {
      fetchNotifications(1);
      setLoaded(true);
    }
  }, [inboxOpen, loaded, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setInboxOpen(false);
      }
    };
    if (inboxOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [inboxOpen, setInboxOpen]);

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    if (n.link) {
      navigate(n.link);
      setInboxOpen(false);
    }
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-container">
      {/* Bell Button */}
      <button
        onClick={() => setInboxOpen(!inboxOpen)}
        className="relative p-2 rounded-lg text-slate-300 hover:bg-white/10 hover:text-white transition-all"
        data-testid="notification-bell"
      >
        {unreadCount > 0 ? (
          <BellRinging size={22} weight="fill" className="text-amber-400 animate-pulse" />
        ) : (
          <Bell size={22} weight="duotone" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Inbox Panel */}
      {inboxOpen && (
        <div className="absolute left-0 top-12 w-[380px] max-h-[520px] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 z-50 flex flex-col overflow-hidden" data-testid="notification-inbox">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <div className="flex items-center gap-2">
              <Bell size={18} weight="fill" className="text-slate-700 dark:text-slate-300" />
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-200" style={{ fontFamily: 'Outfit' }}>Notifications</span>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">{unreadCount}</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={toggleSound} className="p-1.5 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" title={soundEnabled ? 'Mute sounds' : 'Enable sounds'} data-testid="toggle-sound">
                {soundEnabled ? <SpeakerHigh size={16} className="text-slate-600 dark:text-slate-400" /> : <SpeakerSlash size={16} className="text-slate-400" />}
              </button>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="p-1.5 rounded-md hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-blue-600 dark:text-blue-400" title="Mark all as read" data-testid="mark-all-read">
                  <Check size={16} weight="bold" />
                </button>
              )}
            </div>
          </div>

          {/* Notification List */}
          <div className="flex-1 overflow-y-auto" data-testid="notification-list">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Bell size={40} weight="thin" className="mb-2" />
                <span className="text-sm">No notifications yet</span>
              </div>
            ) : (
              notifications.map(n => {
                const Icon = NOTIF_ICONS[n.type] || Bell;
                const colorClass = NOTIF_COLORS[n.type] || 'text-slate-500 bg-slate-50';
                return (
                  <div
                    key={n.id}
                    className={`group flex items-start gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700/50 cursor-pointer transition-colors ${n.read ? 'bg-white dark:bg-slate-800' : 'bg-blue-50/50 dark:bg-blue-900/10'} hover:bg-slate-50 dark:hover:bg-slate-700/30`}
                    onClick={() => handleClick(n)}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${colorClass}`}>
                      <Icon size={16} weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium truncate ${n.read ? 'text-slate-700 dark:text-slate-300' : 'text-slate-900 dark:text-white'}`}>{n.title}</span>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">{n.message}</p>
                      <span className="text-[10px] text-slate-400 mt-1 block">{timeAgo(n.created_at)}</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                      className="shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
                      data-testid={`delete-notification-${n.id}`}
                    >
                      <Trash size={14} className="text-red-400" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
